export type Mode = 'SBR' | 'CPDLPD' | 'IntruderDetection';

export interface ZoneInputs {
    sensorXOffset: number;
    yRef: number;
    y1: number;
    y2: number;
    y3: number;
    theta: number;
    omega: number;
    sensorZOffset: number;
    ceilingHeight: number;
    seat1Height: number;
    seat2Height: number;
    legHeight: number;
    d1ChairSlope: number;
    seat1ChairDepth: number;
    seat1LegRoom: number;
    seat1FootwellDepth: number;
    seat2ChairDepth: number;
    seat2LegRoom: number;
    seat2FootwellDepth: number;
    seatHeightExpandMargin: number;
    seat2FootwellExpandMargin: number;
    seat2RearFacingExpandMargin: number;
    m1: number;
    m2: number;
    m3: number;
    m4: number;
    mode: Mode;
}

export interface Cuboid {
    seatIdx: number;
    zoneIdx: number;
    label: string;
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
    zMin: number;
    zMax: number;
}

export interface SensorPosition {
    xOffset: number;
    yOffset: number;
    zOffset: number;
    azimTilt: number;
    elevTilt: number;
}

export interface OccupancyBox {
    id: number;
    label: string;
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
    zMin: number;
    zMax: number;
}

export interface ZoneOutput {
    sensorPosition: SensorPosition;
    cuboids: Cuboid[];
    occupancyBoxes: OccupancyBox[];
}

const SEAT_LABELS = ['Driver (Row1)', 'Passenger (Row1)', '2nd Row Driver', 'Middle (Row2)', '2nd Row Passenger'];
const ZONE_LABELS = ['Body', 'Leg', 'Footwell'];

export const DEFAULT_INPUTS_SBR: ZoneInputs = {
    sensorXOffset: 0,
    yRef: 0.9,
    y1: 0.2,
    y2: 1.05,
    y3: 0.4,
    theta: -60,
    omega: 0,
    sensorZOffset: 1.1,
    ceilingHeight: 1.2,
    seat1Height: 0.3,
    seat2Height: 0.35,
    legHeight: 0.6,
    d1ChairSlope: 0.16,
    seat1ChairDepth: 0.5,
    seat1LegRoom: 0.3,
    seat1FootwellDepth: 0.6,
    seat2ChairDepth: 0.6,
    seat2LegRoom: 0.2,
    seat2FootwellDepth: 0.3,
    seatHeightExpandMargin: 0,
    seat2FootwellExpandMargin: 0.1,
    seat2RearFacingExpandMargin: 0,
    m1: 0.16,
    m2: 0,
    m3: 0,
    m4: 0,
    mode: 'SBR',
};

export const DEFAULT_INPUTS_CPDLPD: ZoneInputs = {
    ...DEFAULT_INPUTS_SBR,
    seatHeightExpandMargin: 0.2,
    seat2FootwellExpandMargin: 0.2,
    seat2RearFacingExpandMargin: 0.15,
    mode: 'CPDLPD',
};

export const DEFAULT_INPUTS_INTRUDER_DETECTION: ZoneInputs = {
    ...DEFAULT_INPUTS_SBR,
    mode: 'IntruderDetection',
};

/* Default occupancy boxes for Intruder Detection mode, matching
 * profile_8T8R_tdm_6888_intrusion_Tesla_test.cfg (boxes B1-B11). The cfg's
 * unlabeled box 0 (base/boundary box) is intentionally omitted here since
 * it carries no independent zone semantics for this tool. */
export const DEFAULT_OCCUPANCY_BOXES: OccupancyBox[] = [
    { id: 1,  label: 'B1 - Driver dashboard',          xMin: 0.00,  xMax: 0.55,  yMin: -0.80, yMax: -0.20, zMin: 0.50, zMax: 1.15 },
    { id: 2,  label: 'B2 - Passenger dashboard',        xMin: -0.55, xMax: 0.00,  yMin: -0.80, yMax: -0.20, zMin: 0.50, zMax: 1.15 },
    { id: 3,  label: 'B3 - Driver front',               xMin: 0.00,  xMax: 0.55,  yMin: -0.20, yMax: 0.60,  zMin: 0.40, zMax: 1.10 },
    { id: 4,  label: 'B4 - Passenger front',            xMin: -0.55, xMax: 0.00,  yMin: -0.20, yMax: 0.60,  zMin: 0.40, zMax: 1.10 },
    { id: 5,  label: 'B5 - Driver back',                xMin: 0.00,  xMax: 0.55,  yMin: 0.60,  yMax: 1.50,  zMin: 0.40, zMax: 1.10 },
    { id: 6,  label: 'B6 - Passenger back',              xMin: -0.55, xMax: 0.00,  yMin: 0.60,  yMax: 1.50,  zMin: 0.40, zMax: 1.10 },
    { id: 7,  label: 'B7 - Driver proximity front',      xMin: 0.70,  xMax: 1.10,  yMin: -0.80, yMax: 0.60,  zMin: 0.70, zMax: 1.10 },
    { id: 8,  label: 'B8 - Driver proximity back',       xMin: 0.70,  xMax: 1.10,  yMin: 0.60,  yMax: 1.85,  zMin: 0.70, zMax: 1.10 },
    { id: 9,  label: 'B9 - Passenger proximity front',   xMin: -1.20, xMax: -0.70, yMin: -0.80, yMax: 0.60,  zMin: 0.70, zMax: 1.10 },
    { id: 10, label: 'B10 - Passenger proximity back',   xMin: -1.20, xMax: -0.70, yMin: 0.60,  yMax: 1.85,  zMin: 0.70, zMax: 1.10 },
    { id: 11, label: 'B11 - Trunk',                      xMin: -0.62, xMax: 0.62,  yMin: 1.60,  yMax: 2.10,  zMin: 0.50, zMax: 1.40 },
];

export function computeZones(inp: ZoneInputs, occupancyBoxes: OccupancyBox[] = []): ZoneOutput {
    const {
        sensorXOffset,
        yRef, y1, y2, y3, theta, omega, sensorZOffset, ceilingHeight,
        seat1Height, seat2Height, legHeight,
        d1ChairSlope, seat1ChairDepth, seat1LegRoom, seat1FootwellDepth,
        seat2LegRoom, seat2FootwellDepth,
        seatHeightExpandMargin, seat2FootwellExpandMargin, seat2RearFacingExpandMargin,
        m1, m2, m3, m4,
        mode,
    } = inp;

    const sensorPosition: SensorPosition = {
        xOffset: sensorXOffset,
        yOffset: yRef - y1,
        zOffset: sensorZOffset,
        azimTilt: omega,
        elevTilt: theta,
    };

    const cuboids: Cuboid[] = [];

    const seat2FootwellYMin = mode === 'SBR'
        ? yRef + y3 - seat2FootwellDepth - seat2FootwellExpandMargin
        : yRef + y3 - seat2FootwellDepth;

    /* Seat/zone cuboids only apply to SBR and CPD/LPD modes. Intruder
     * Detection uses occupancy boxes instead (see below). */
    if (mode !== 'IntruderDetection') {

    /* Seat 0: Driver (xMin=0.15, xMax=0.7) */
    cuboids.push({
        seatIdx: 0, zoneIdx: 0, label: `${SEAT_LABELS[0]} - ${ZONE_LABELS[0]}`,
        xMin: 0.15, xMax: 0.7,
        yMin: yRef - seat1ChairDepth + 0.05, yMax: yRef + d1ChairSlope,
        zMin: legHeight, zMax: ceilingHeight,
    });
    cuboids.push({
        seatIdx: 0, zoneIdx: 1, label: `${SEAT_LABELS[0]} - ${ZONE_LABELS[1]}`,
        xMin: 0.15, xMax: 0.7,
        yMin: yRef - seat1ChairDepth - seat1LegRoom, yMax: yRef,
        zMin: seat1Height - seatHeightExpandMargin, zMax: legHeight,
    });
    cuboids.push({
        seatIdx: 0, zoneIdx: 2, label: `${SEAT_LABELS[0]} - ${ZONE_LABELS[2]}`,
        xMin: 0.15, xMax: 0.7,
        yMin: yRef - seat1ChairDepth - seat1FootwellDepth, yMax: yRef - 0.4,
        zMin: -0.2, zMax: seat1Height,
    });

    /* Seat 1: Passenger (xMin=-0.7, xMax=-0.15) — mirror of Seat 0 */
    cuboids.push({
        seatIdx: 1, zoneIdx: 0, label: `${SEAT_LABELS[1]} - ${ZONE_LABELS[0]}`,
        xMin: -0.7, xMax: -0.15,
        yMin: yRef - seat1ChairDepth + 0.05, yMax: yRef + d1ChairSlope,
        zMin: legHeight, zMax: ceilingHeight,
    });
    cuboids.push({
        seatIdx: 1, zoneIdx: 1, label: `${SEAT_LABELS[1]} - ${ZONE_LABELS[1]}`,
        xMin: -0.7, xMax: -0.15,
        yMin: yRef - seat1ChairDepth - seat1LegRoom, yMax: yRef,
        zMin: seat1Height - seatHeightExpandMargin, zMax: legHeight,
    });
    cuboids.push({
        seatIdx: 1, zoneIdx: 2, label: `${SEAT_LABELS[1]} - ${ZONE_LABELS[2]}`,
        xMin: -0.7, xMax: -0.15,
        yMin: yRef - seat1ChairDepth - seat1FootwellDepth, yMax: yRef - 0.4,
        zMin: -0.2, zMax: seat1Height,
    });

    /* Seat 2: 2nd row driver (xMin=0.2, xMax=0.7) */
    cuboids.push({
        seatIdx: 2, zoneIdx: 0, label: `${SEAT_LABELS[2]} - ${ZONE_LABELS[0]}`,
        xMin: 0.2, xMax: 0.7,
        yMin: yRef + y3 + 0.1, yMax: yRef + y2 + seat2RearFacingExpandMargin,
        zMin: legHeight, zMax: ceilingHeight,
    });
    cuboids.push({
        seatIdx: 2, zoneIdx: 1, label: `${SEAT_LABELS[2]} - ${ZONE_LABELS[1]}`,
        xMin: 0.2, xMax: 0.7,
        yMin: yRef + y3 - seat2LegRoom, yMax: yRef + y2 + seat2RearFacingExpandMargin,
        zMin: seat2Height - seatHeightExpandMargin, zMax: legHeight,
    });
    cuboids.push({
        seatIdx: 2, zoneIdx: 2, label: `${SEAT_LABELS[2]} - ${ZONE_LABELS[2]}`,
        xMin: 0.2, xMax: 0.7,
        yMin: seat2FootwellYMin, yMax: yRef + y3 + seat2FootwellExpandMargin,
        zMin: -seat2FootwellExpandMargin, zMax: seat2Height,
    });

    /* Seat 3: Middle (xMin=-0.12, xMax=0.12) */
    cuboids.push({
        seatIdx: 3, zoneIdx: 0, label: `${SEAT_LABELS[3]} - ${ZONE_LABELS[0]}`,
        xMin: -0.12, xMax: 0.12,
        yMin: yRef + y3 + 0.1, yMax: yRef + y2 - m4,
        zMin: seat2Height, zMax: ceilingHeight,
    });
    cuboids.push({
        seatIdx: 3, zoneIdx: 1, label: `${SEAT_LABELS[3]} - ${ZONE_LABELS[1]}`,
        xMin: -0.12, xMax: 0.12,
        yMin: yRef + m1, yMax: yRef + y3 - m2,
        zMin: m3, zMax: legHeight,
    });
    cuboids.push({
        seatIdx: 3, zoneIdx: 2, label: `${SEAT_LABELS[3]} - ${ZONE_LABELS[2]}`,
        xMin: -0.12, xMax: 0.12,
        yMin: yRef + y3 - seat2FootwellDepth, yMax: yRef + y3,
        zMin: m3, zMax: seat2Height - m2,
    });

    /* Seat 4: 2nd row passenger (xMin=-0.7, xMax=-0.2) — mirror of Seat 2 */
    cuboids.push({
        seatIdx: 4, zoneIdx: 0, label: `${SEAT_LABELS[4]} - ${ZONE_LABELS[0]}`,
        xMin: -0.7, xMax: -0.2,
        yMin: yRef + y3 + 0.1, yMax: yRef + y2 + seat2RearFacingExpandMargin,
        zMin: legHeight, zMax: ceilingHeight,
    });
    cuboids.push({
        seatIdx: 4, zoneIdx: 1, label: `${SEAT_LABELS[4]} - ${ZONE_LABELS[1]}`,
        xMin: -0.7, xMax: -0.2,
        yMin: yRef + y3 - seat2LegRoom, yMax: yRef + y2 + seat2RearFacingExpandMargin,
        zMin: seat2Height - seatHeightExpandMargin, zMax: legHeight,
    });
    cuboids.push({
        seatIdx: 4, zoneIdx: 2, label: `${SEAT_LABELS[4]} - ${ZONE_LABELS[2]}`,
        xMin: -0.7, xMax: -0.2,
        yMin: seat2FootwellYMin, yMax: yRef + y3 + seat2FootwellExpandMargin,
        zMin: -seat2FootwellExpandMargin, zMax: seat2Height,
    });

    } /* mode !== 'IntruderDetection' */

    /* Intruder Detection: occupancy boxes come from caller-managed state
     * (editable in the UI), not derived from seat/vehicle geometry. */
    const activeOccupancyBoxes = mode === 'IntruderDetection' ? occupancyBoxes : [];

    return { sensorPosition, cuboids, occupancyBoxes: activeOccupancyBoxes };
}

function fmt(v: number): string {
    return Number(v.toFixed(3)).toString();
}

/* Parse CLI commands from text input and extract parameters.
 * Supported commands per mode:
 * - All modes: sensorPosition xOffset yOffset zOffset azimTilt elevTilt
 * - SBR/CPDLPD: cuboidDef seatIdx zoneIdx xMin xMax yMin yMax zMin zMax
 * - IntruderDetection: occupancyBox id xMin xMax yMin yMax zMin zMax
 * Returns updated ZoneInputs, OccupancyBox[], and custom Cuboids (for SBR/CPDLPD).
 * Only the specific items mentioned in CLI commands are modified; others remain unchanged. */
export function parseCLICommands(
    text: string,
    currentInputs: ZoneInputs,
    currentOccupancyBoxes: OccupancyBox[],
    currentCustomCuboids: Cuboid[],
    mode: Mode
): { inputs: ZoneInputs; occupancyBoxes: OccupancyBox[]; customCuboids: Cuboid[]; parsedCount: number } {
    const lines = text.split('\n');
    let inputs = { ...currentInputs };
    // Start with copies of current state - only modify what CLI commands specify
    let occupancyBoxes = [...currentOccupancyBoxes];
    let customCuboids = [...currentCustomCuboids];
    let parsedCount = 0;

    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('%') || trimmed.startsWith('#')) {
            continue; // Skip empty lines and comments
        }

        const parts = trimmed.split(/\s+/);
        const cmd = parts[0].toLowerCase();

        // sensorPosition - supported in all modes (last one wins)
        if (cmd === 'sensorposition' && parts.length >= 6) {
            const xOffset = parseFloat(parts[1]);
            const yOffset = parseFloat(parts[2]);
            const zOffset = parseFloat(parts[3]);
            const azimTilt = parseFloat(parts[4]);
            const elevTilt = parseFloat(parts[5]);

            if (!isNaN(xOffset) && !isNaN(yOffset) && !isNaN(zOffset) &&
                !isNaN(azimTilt) && !isNaN(elevTilt)) {
                // sensorPosition uses yOffset (sensor position relative to world origin)
                // Our internal representation uses yRef (fixed at 0.9) and y1 (sensor offset from yRef)
                // yOffset = yRef - y1, so y1 = yRef - yOffset
                inputs = {
                    ...inputs,
                    sensorXOffset: xOffset,
                    y1: inputs.yRef - yOffset,
                    sensorZOffset: zOffset,
                    omega: azimTilt,
                    theta: elevTilt,
                };
                parsedCount++;
            }
        }
        // cuboidDef - supported in SBR and CPDLPD modes
        else if (cmd === 'cuboiddef' && parts.length >= 9 && (mode === 'SBR' || mode === 'CPDLPD')) {
            const seatIdx = parseInt(parts[1], 10);
            const zoneIdx = parseInt(parts[2], 10);
            const xMin = parseFloat(parts[3]);
            const xMax = parseFloat(parts[4]);
            const yMin = parseFloat(parts[5]);
            const yMax = parseFloat(parts[6]);
            const zMin = parseFloat(parts[7]);
            const zMax = parseFloat(parts[8]);

            if (!isNaN(seatIdx) && !isNaN(zoneIdx) &&
                !isNaN(xMin) && !isNaN(xMax) && !isNaN(yMin) && !isNaN(yMax) && !isNaN(zMin) && !isNaN(zMax)) {
                const seatLabel = SEAT_LABELS[seatIdx] ?? `Seat ${seatIdx}`;
                const zoneLabel = ZONE_LABELS[zoneIdx] ?? `Zone ${zoneIdx}`;
                // Update or add this specific cuboid (by seatIdx + zoneIdx)
                const existingIndex = customCuboids.findIndex(c => c.seatIdx === seatIdx && c.zoneIdx === zoneIdx);
                const newCuboid: Cuboid = {
                    seatIdx,
                    zoneIdx,
                    label: `${seatLabel} - ${zoneLabel}`,
                    xMin, xMax, yMin, yMax, zMin, zMax,
                };

                if (existingIndex >= 0) {
                    customCuboids = customCuboids.map((c, i) =>
                        i === existingIndex ? newCuboid : c
                    );
                } else {
                    customCuboids = [...customCuboids, newCuboid];
                }
                parsedCount++;
            }
        }
        // occupancyBox - supported in IntruderDetection mode
        else if (cmd === 'occupancybox' && parts.length >= 7 && mode === 'IntruderDetection') {
            const id = parseInt(parts[1], 10);
            const xMin = parseFloat(parts[2]);
            const xMax = parseFloat(parts[3]);
            const yMin = parseFloat(parts[4]);
            const yMax = parseFloat(parts[5]);
            const zMin = parseFloat(parts[6]);
            const zMax = parseFloat(parts[7]);

            if (!isNaN(id) && !isNaN(xMin) && !isNaN(xMax) &&
                !isNaN(yMin) && !isNaN(yMax) && !isNaN(zMin) && !isNaN(zMax)) {
                // Preserve original label from current occupancy boxes if it exists
                const existingBox = occupancyBoxes.find(b => b.id === id);
                const newBox: OccupancyBox = {
                    id,
                    label: existingBox?.label ?? `Box ${id}`,
                    xMin, xMax, yMin, yMax, zMin, zMax,
                };
                // Update or add this specific occupancy box (by id)
                const existingIndex = occupancyBoxes.findIndex(b => b.id === id);
                if (existingIndex >= 0) {
                    occupancyBoxes = occupancyBoxes.map((b, i) =>
                        i === existingIndex ? newBox : b
                    );
                } else {
                    occupancyBoxes = [...occupancyBoxes, newBox];
                }
                parsedCount++;
            }
        }
        // Unsupported commands are silently ignored (filtered out)
    }

    return { inputs, occupancyBoxes, customCuboids, parsedCount };
}

export function formatOutput(output: ZoneOutput): string {
    const sp = output.sensorPosition;
    const lines: string[] = [
        `sensorPosition ${fmt(sp.xOffset)} ${fmt(sp.yOffset)} ${fmt(sp.zOffset)} ${fmt(sp.azimTilt)} ${fmt(sp.elevTilt)}`,
    ];

    for (const c of output.cuboids) {
        lines.push(
            `cuboidDef ${c.seatIdx} ${c.zoneIdx} ${fmt(c.xMin)} ${fmt(c.xMax)} ${fmt(c.yMin)} ${fmt(c.yMax)} ${fmt(c.zMin)} ${fmt(c.zMax)}`
        );
    }

    for (const b of output.occupancyBoxes) {
        lines.push(
            `occupancyBox ${b.id} ${fmt(b.xMin)} ${fmt(b.xMax)} ${fmt(b.yMin)} ${fmt(b.yMax)} ${fmt(b.zMin)} ${fmt(b.zMax)}`
        );
    }

    return lines.join('\n');
}
